import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  FlatList,
  Alert
} from 'react-native';
import { observer } from '@legendapp/state/react';
import { Redirect, useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SettingsPaymentsLogger } from '../../../../utils/logger';
import theme from '../../../../config/theme';
import { globalState } from '../../../../state/globalState';
import { getUserPayments } from '../../../../services/subscriptionService';
import { useWebAlert } from '../../../../components/WebAlert';
import UpgradePrompt from '@/components/subscription/UpgradePrompt';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { hasPremiumAccess } from '@/utils/subscriptionAccess';
import { canAccessFinancialSettings } from '@/utils/userRoles';

// Logger para a página de pagamentos
const logger = SettingsPaymentsLogger.Main;

// Página principal de pagamentos
const PaymentsScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const listBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const company = globalState.company.data.get();
  const profile = globalState.user.profile.get();
  const userType = profile?.user_type;
  const { showAlert } = useWebAlert();
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const subscriptionSnapshot = globalState.subscription.get();
  const premiumAccess = hasPremiumAccess(subscriptionSnapshot, profile);
  const hasScreenAccess = canAccessFinancialSettings(userType);

  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string) => {
    if (Platform.OS !== 'android') {
      showAlert(title, msg, [{ text: 'OK' }]);
    } else {
      Alert.alert(title, msg);
    }
  };
  
  useEffect(() => {
    logger.debug('Página de pagamentos inicializada');

    if (!hasScreenAccess) {
      setLoading(false);
      setPayments([]);
      return () => {
        logger.debug('Página de pagamentos desmontada');
      };
    }

    if (!premiumAccess) {
      setLoading(false);
      setPayments([]);
      return () => {
        logger.debug('Página de pagamentos desmontada');
      };
    }

    const loadPayments = async () => {
      try {
        logger.debug('Carregando histórico de pagamentos');
        setLoading(true);

        const empresaId = company?.id || profile?.empresa_id;

        if (!empresaId) {
          logger.warn('Empresa não encontrada para o usuário atual');
          showMessage('Atenção', 'Não foi possível localizar a empresa vinculada ao usuário.');
          setPayments([]);
          setLoading(false);
          return;
        }

        const { data: paymentHistory, error } = await getUserPayments(empresaId);

        if (error) {
          logger.error('Erro ao carregar pagamentos:', error);
          showMessage('Aviso', 'Não foi possível sincronizar os pagamentos mais recentes. Mostrando dados offline.');
        }

        setPayments(paymentHistory || []);
        setLoading(false);
      } catch (error) {
        logger.error('Erro ao carregar pagamentos:', error);
        showMessage('Erro', 'Ocorreu um erro ao carregar os dados.');
        setLoading(false);
      }
    };

    loadPayments();

    return () => {
      logger.debug('Página de pagamentos desmontada');
    };
  }, [hasScreenAccess, premiumAccess, company?.id, profile?.empresa_id]);

  // Funções utilitárias para formatação
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('pt-BR');
  };

  // Obter informações de status com ícone
  const getStatusInfo = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'approved':
      case 'success':
      case 'paid':
        return {
          icon: 'checkmark-circle',
          color: theme.colors.util.success,
          label: 'Pago'
        };
      case 'pending':
      case 'processing':
        return {
          icon: 'time',
          color: theme.colors.util.warning,
          label: 'Pendente'
        };
      case 'failed':
      case 'error':
      case 'cancelled':
        return {
          icon: 'close-circle',
          color: theme.colors.util.error,
          label: 'Falhou'
        };
      default:
        return {
          icon: 'help-circle',
          color: theme.colors.text.secondary,
          label: status || 'Desconhecido'
        };
    }
  };
  
  // Funções para ações
  const handleAddPaymentMethod = () => {
    logger.debug('Adicionar forma de pagamento solicitado');
    // Aqui iria a lógica para adicionar uma nova forma de pagamento
  };
  
  // Navegação para voltar
  const handleGoBack = () => {
    router.back();
  };

  // Renderizar um item de pagamento
  const renderPaymentItem = ({ item }: { item: any }) => {
    const statusInfo = getStatusInfo(item.status);
    
    return (
      <View style={styles.paymentItem}>
        <View style={styles.paymentHeader}>
          <Text style={styles.paymentDate}>{item.payment_date ? formatDate(item.payment_date) : 'N/A'}</Text>
          <View style={[styles.paymentStatus, { backgroundColor: statusInfo.color }]}>
            <Text style={styles.paymentStatusText}>{statusInfo.label}</Text>
          </View>
        </View>
        
        <View style={styles.paymentDetails}>
          <View style={styles.paymentInfo}>
            <Text style={styles.paymentLabel}>Valor:</Text>
            <Text style={styles.paymentValue}>{formatAmount(item.amount)}</Text>
          </View>
          
          <View style={styles.paymentInfo}>
            <Text style={styles.paymentLabel}>Método:</Text>
            <Text style={styles.paymentValue}>{item.method || 'N/A'}</Text>
          </View>

          {item.transaction_reference && (
            <View style={styles.paymentInfo}>
              <Text style={styles.paymentLabel}>Referência:</Text>
              <Text style={styles.paymentValue}>{item.transaction_reference}</Text>
            </View>
          )}
        </View>
      </View>
    );
  };

  if (!hasScreenAccess) {
    return <Redirect href="/(app)/settings" />;
  }

  if (!premiumAccess) {
    return (
      <View style={[styles.container, styles.restrictedWrapper]}>
        <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleGoBack}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Pagamentos</Text>
          <View style={styles.headerRight} />
        </View>
        <UpgradePrompt variant="restricted" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={handleGoBack}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Pagamentos</Text>
        <View style={styles.headerRight} />
      </View>
      
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary.default} />
          <Text style={styles.loadingText}>Carregando histórico de pagamentos...</Text>
        </View>
      ) : (
        <>                    
          <Text style={styles.historyTitle}>Histórico de pagamentos</Text>
          
          {payments.length === 0 ? (
            <View style={styles.emptyContainer}>
              <MaterialCommunityIcons name={"receipt-text" as any} size={48} color={theme.colors.text.tertiary} />
              <Text style={styles.emptyText}>Nenhum pagamento encontrado</Text>
            </View>
          ) : (
            <FlatList
              data={payments}
              keyExtractor={(item) => item.id || String(item.payment_date)}
              renderItem={renderPaymentItem}
              contentContainerStyle={[styles.listContainer, { paddingBottom: listBottomPadding }]}
            />
          )}
        </>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  restrictedWrapper: {
    justifyContent: 'flex-start',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  headerRight: {
    width: 40,
  },
  section: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.lg,
    ...theme.shadows.md,
  },
  sectionTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.md,
  },
  paymentMethod: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  paymentMethodText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    marginLeft: theme.spacing.md,
  },
  addButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    alignItems: 'center',
    marginTop: theme.spacing.lg,
  },
  addButtonText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  historyTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    margin: theme.spacing.md,
    marginBottom: 0,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl,
  },
  emptyText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.tertiary,
    marginTop: theme.spacing.md,
    textAlign: 'center',
  },
  listContainer: {
    padding: theme.spacing.md,
  },
  paymentItem: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
    ...theme.shadows.sm,
  },
  paymentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
  },
  paymentDate: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.primary,
  },
  paymentStatus: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 3,
    borderRadius: theme.borderRadius.circular,
  },
  paymentStatusText: {
    fontSize: theme.fontSizes.xs,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.inverse,
  },
  paymentDetails: {
    marginTop: theme.spacing.sm,
  },
  paymentInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  paymentLabel: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  paymentValue: {
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.primary,
  },
});

export default PaymentsScreen; 
